import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/Page/TextLayer.css';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import { 
  ArrowLeft, Play, Square, Users, FileText, 
  Loader2, ClipboardList, Calendar, Timer,
  MessageSquare, PenLine, AlertCircle, Plus, BarChart3, Edit2, Pause,
  RotateCcw, RefreshCw, Upload, Sparkles, CheckCircle
} from 'lucide-react';
import api, { API_BASE_URL } from '../utils/api';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';

pdfjs.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.mjs';

const MAX_DOC_TABS = 5;

const normalizeDocumentForTab = (doc) => {
  const documentId = doc.document_id || doc.id;
  const proxyUrl = documentId ? `${API_BASE_URL}/api/board/documents/${documentId}/content-proxy` : null;
  const rawContentUrl = doc.content_url || doc.preview_url || doc.pdf_preview_url || doc.file_url || doc.url || proxyUrl;
  const contentUrl = rawContentUrl?.startsWith('/api/')
    ? `${API_BASE_URL}${rawContentUrl}`
    : rawContentUrl;
  const safeContentUrl = documentId ? proxyUrl : contentUrl;
  return {
    document_id: documentId,
    title: doc.title || doc.name || 'Document',
    file_type: doc.file_type || doc.mime_type || '',
    file_url: safeContentUrl,
    preview_url: safeContentUrl
  };
};

const isPdfType = (fileType) => fileType?.includes('pdf');
const isImageType = (fileType) => fileType?.includes('image');
const withPdfZoom = (url) => {
  if (!url) return url;
  if (url.includes('zoom=')) return url;
  const params = 'zoom=90&view=FitH&pagemode=none&navpanes=0';
  return url.includes('#') ? `${url}&${params}` : `${url}#${params}`;
};

// Import modular meeting components
import {
  formatDuration,
  formatTime,
  MeetingClock,
  AgendaItemRow,
  GeneralAdminIndicator,
  QuorumIndicator,
  AttendancePanel,
  MotionsPanel,
  ParkingLotPanel,
  TimeAnalyticsPanel,
  ResizableMeetingLayout,
  MeetingQuickLinks
} from './meeting';

// Import agenda creator dialog
import { CreateAgendaDialog } from './Governance';

// =============================================================================
// MAIN LIVE MEETING RUNNER COMPONENT
// =============================================================================

const MeetingPdfPreview = ({ url, title }) => {
  const [numPages, setNumPages] = useState(null);
  const [error, setError] = useState(null);
  const [width, setWidth] = useState(760);
  const containerRef = useRef(null);

  useEffect(() => {
    const updateWidth = () => {
      if (containerRef.current) {
        setWidth(Math.max(320, Math.min(containerRef.current.clientWidth - 32, 980)));
      }
    };
    updateWidth();
    const observer = new ResizeObserver(updateWidth);
    if (containerRef.current) observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <div ref={containerRef} className="h-full overflow-auto bg-gray-100 p-4">
      {error ? (
        <div className="h-full flex flex-col items-center justify-center text-gray-600">
          <AlertCircle className="h-10 w-10 mb-3 text-red-500" />
          <p className="font-medium">Unable to render PDF preview</p>
          <p className="text-sm mt-1">{String(error.message || error)}</p>
        </div>
      ) : (
        <Document
          file={url}
          onLoadSuccess={({ numPages }) => setNumPages(numPages)}
          onLoadError={setError}
          loading={<div className="flex items-center justify-center h-full text-gray-600"><Loader2 className="h-8 w-8 mr-2 animate-spin" /> Loading PDF...</div>}
          error={<div className="text-center text-red-600">Failed to load {title}</div>}
        >
          {Array.from(new Array(numPages || 0), (_el, index) => (
            <div key={`page_${index + 1}`} className="mb-4 flex justify-center">
              <Page
                pageNumber={index + 1}
                width={width}
                renderTextLayer={false}
                renderAnnotationLayer={false}
                loading={<div className="h-96 flex items-center justify-center bg-white shadow rounded"><Loader2 className="h-6 w-6 animate-spin text-purple-600" /></div>}
              />
            </div>
          ))}
        </Document>
      )}
    </div>
  );
};

const LiveMeetingRunner = () => {
  const { meetingId } = useParams();
  const navigate = useNavigate();
  
  // State
  const [meeting, setMeeting] = useState(null);
  const [agendaItems, setAgendaItems] = useState([]);
  const [timing, setTiming] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  
  // Attendance state
  const [showAttendance, setShowAttendance] = useState(false);
  const [quorum, setQuorum] = useState(null);
  
  // Board members for assignments
  const [boardMembers, setBoardMembers] = useState([]);
  
  // Note-taking mode
  const [noteTakingMode, setNoteTakingMode] = useState(false);
  const [agendaNotes, setAgendaNotes] = useState({});
  const [savingNotes, setSavingNotes] = useState({});
  const [notesReadOnly, setNotesReadOnly] = useState(false);
  const [hasNotesLock, setHasNotesLock] = useState(false);
  
  // Motions state
  const [showMotions, setShowMotions] = useState(false);

  const [meetingDocs, setMeetingDocs] = useState([]);
  const [docTabs, setDocTabs] = useState([]);
  const [activeTabId, setActiveTabId] = useState('agenda');
  const [docBlobUrls, setDocBlobUrls] = useState({});
  const [docBlobLoading, setDocBlobLoading] = useState({});
  
  // Parking lot state
  const [showParkingLot, setShowParkingLot] = useState(false);
  
  // Agenda creator state
  const [showAgendaCreator, setShowAgendaCreator] = useState(false);
  
  // Time analytics state
  const [showTimeAnalytics, setShowTimeAnalytics] = useState(false);
  
  // Edit meeting state
  const [showEditMeeting, setShowEditMeeting] = useState(false);
  const [editData, setEditData] = useState({});
  const [saving, setSaving] = useState(false);
  const [canGenerateFollowupTasks, setCanGenerateFollowupTasks] = useState(false);
  const [generatingFollowupTasks, setGeneratingFollowupTasks] = useState(false);
  const [resolvedCommitteeId, setResolvedCommitteeId] = useState(null);

  // Transcript draft state
  const [showTranscriptDialog, setShowTranscriptDialog] = useState(false);
  const [transcriptText, setTranscriptText] = useState('');
  const [transcriptSourceName, setTranscriptSourceName] = useState('');
  const [generatingTranscriptDraft, setGeneratingTranscriptDraft] = useState(false);
  const [applyingTranscriptDraft, setApplyingTranscriptDraft] = useState(false);
  const [generatingMinutesReviewDoc, setGeneratingMinutesReviewDoc] = useState(false);
  const [minutesReviewDocumentId, setMinutesReviewDocumentId] = useState(null);
  const [transcriptDraft, setTranscriptDraft] = useState(null);
  const [replaceExistingTranscriptNotes, setReplaceExistingTranscriptNotes] = useState(false);
  const [createTranscriptActionItems, setCreateTranscriptActionItems] = useState(true);
  
  // Polling refs
  const pollingRef = useRef(null);
  const attendancePollingRef = useRef(null);
  const isChair = true; // TODO: Implement proper role check
  
  // Fetch board members for action item assignment (called once on mount)
  const fetchBoardMembers = useCallback(async () => {
    try {
      const response = await api.get('/board/documents/members');
      setBoardMembers(response.data.members || []);
    } catch (err) {
      console.error('Failed to fetch board members:', err);
    }
  }, []);

  // Redirect to committee meeting runner when meeting belongs to a committee
  useEffect(() => {
    const resolveCommitteeMeeting = async () => {
      if (!meetingId || resolvedCommitteeId) return;
      try {
        const res = await api.get(`/governance/committees/meetings/${meetingId}/resolve`);
        if (res.data?.committee_id) {
          setResolvedCommitteeId(res.data.committee_id);
          navigate(`/governance/committees/${res.data.committee_id}/meetings/${meetingId}`, { replace: true });
        }
      } catch (err) {
        if (err.response?.status !== 404) {
          console.error('Failed to resolve committee meeting:', err);
        }
      }
    };
    resolveCommitteeMeeting();
  }, [meetingId, navigate, resolvedCommitteeId]);

  const fetchEditStatus = useCallback(async () => {
    try {
      const res = await api.get(`/board/meetings/${meetingId}/minutes/edit-status`);
      setCanGenerateFollowupTasks(!!res.data?.can_generate_followup_tasks);
    } catch (err) {
      console.error('Failed to fetch edit status:', err);
    }
  }, [meetingId]);
  
  // Fetch attendance/quorum
  const fetchAttendance = useCallback(async () => {
    try {
      const response = await api.get(`/board/meetings/${meetingId}/attendance`);
      setQuorum(response.data.quorum);
    } catch (err) {
      // Don't spam console on rate limit errors
      if (err.response?.status !== 429) {
        console.error('Failed to fetch attendance:', err);
      }
    }
  }, [meetingId]);
  
  // Fetch live state - use ref to track if we have data to avoid dependency on meeting state
  const hasLoadedRef = useRef(false);
  const fetchLiveState = useCallback(async () => {
    try {
      const response = await api.get(`/board/meetings/${meetingId}/live-state`);
      setMeeting(response.data.meeting);
      setAgendaItems(response.data.agenda_items || []);
      setTiming(response.data.timing || {});
      setError(null);
      hasLoadedRef.current = true;
    } catch (err) {
      // Don't spam console on rate limit errors
      if (err.response?.status !== 429) {
        console.error('Failed to fetch live state:', err);
      }
      if (!hasLoadedRef.current) {
        setError('Failed to load meeting');
      }
    } finally {
      setLoading(false);
    }
  }, [meetingId]);

  const fetchMeetingDocuments = useCallback(async () => {
    try {
      const res = await api.get(`/board/meetings/${meetingId}/documents`);
      setMeetingDocs(res.data.linked_documents || []);
    } catch (err) {
      console.error('Failed to fetch meeting documents:', err);
    }
  }, [meetingId]);

  const linkedDocsByAgenda = useMemo(() => {
    const grouped = {};
    meetingDocs.forEach((doc) => {
      const key = doc.agenda_item_id || 'general';
      if (!grouped[key]) {
        grouped[key] = [];
      }
      grouped[key].push(doc);
    });
    return grouped;
  }, [meetingDocs]);

  const openDocumentTab = useCallback((doc) => {
    if (!doc) return;
    const normalized = normalizeDocumentForTab(doc);
    setDocTabs((prev) => {
      const exists = prev.find((t) => t.document_id === normalized.document_id);
      if (exists) {
        return prev;
      }
      const next = prev.length >= MAX_DOC_TABS ? [...prev.slice(1), normalized] : [...prev, normalized];
      return next;
    });
    setActiveTabId(normalized.document_id);
  }, []);

  const closeDocTab = (docId) => {
    setDocBlobUrls(prev => {
      if (prev[docId]) URL.revokeObjectURL(prev[docId]);
      const next = { ...prev };
      delete next[docId];
      return next;
    });
    setDocBlobLoading(prev => {
      const next = { ...prev };
      delete next[docId];
      return next;
    });
    setDocTabs((prev) => {
      const next = prev.filter((t) => t.document_id !== docId);
      if (activeTabId === docId) {
        setActiveTabId(next.length > 0 ? next[next.length - 1].document_id : 'agenda');
      }
      return next;
    });
  };

  const resolveDocumentById = useCallback(async (documentId, { preferFresh = false } = {}) => {
    if (!preferFresh) {
      const fromMeeting = meetingDocs.find((d) => d.document_id === documentId || d.id === documentId);
      if (fromMeeting) {
        return normalizeDocumentForTab(fromMeeting);
      }
    }
    try {
      const res = await api.get(`/board/documents/${documentId}`);
      return normalizeDocumentForTab(res.data);
    } catch (err) {
      console.error('Failed to fetch document details:', err);
      const fromMeeting = meetingDocs.find((d) => d.document_id === documentId || d.id === documentId);
      return fromMeeting ? normalizeDocumentForTab(fromMeeting) : null;
    }
  }, [meetingDocs]);

  const handleOpenDocumentById = useCallback(async (documentId, titleOverride, options = {}) => {
    const doc = await resolveDocumentById(documentId, options);
    if (!doc) {
      toast.error('Failed to open document');
      return;
    }
    if (titleOverride && !doc.title) {
      doc.title = titleOverride;
    }
    openDocumentTab(doc);
  }, [openDocumentTab, resolveDocumentById]);

  const handleOpenFirstMeetingDoc = useCallback(() => {
    if (!meetingDocs.length) {
      toast.error('No documents linked to this meeting');
      return;
    }
    openDocumentTab(meetingDocs[0]);
  }, [meetingDocs, openDocumentTab]);
  
  // Store callbacks in refs to avoid dependency issues
  const fetchLiveStateRef = useRef(fetchLiveState);
  const fetchAttendanceRef = useRef(fetchAttendance);
  const fetchBoardMembersRef = useRef(fetchBoardMembers);
  
  // Keep refs updated
  useEffect(() => {
    fetchLiveStateRef.current = fetchLiveState;
    fetchAttendanceRef.current = fetchAttendance;
    fetchBoardMembersRef.current = fetchBoardMembers;
  });
  
  useEffect(() => {
    const activeDoc = docTabs.find((doc) => doc.document_id === activeTabId);
    if (!activeDoc || activeTabId === 'agenda') return;
    if (docBlobUrls[activeDoc.document_id] || docBlobLoading[activeDoc.document_id]) return;

    const loadBlob = async () => {
      setDocBlobLoading(prev => ({ ...prev, [activeDoc.document_id]: true }));
      try {
        const res = await api.get(`/board/documents/${activeDoc.document_id}/content-proxy`, {
          responseType: 'blob'
        });
        const blobUrl = URL.createObjectURL(res.data);
        setDocBlobUrls(prev => ({ ...prev, [activeDoc.document_id]: blobUrl }));
      } catch (err) {
        console.error('Failed to load document preview blob:', err);
        toast.error('Failed to load document preview');
      } finally {
        setDocBlobLoading(prev => ({ ...prev, [activeDoc.document_id]: false }));
      }
    };

    loadBlob();
  }, [activeTabId, docTabs, docBlobUrls, docBlobLoading]);

  useEffect(() => {
    return () => {
      Object.values(docBlobUrls).forEach((url) => URL.revokeObjectURL(url));
    };
  }, [docBlobUrls]);

  // Initial load and polling setup - use meetingId as the only dependency
  useEffect(() => {
    // Reset state when meeting changes
    hasLoadedRef.current = false;
    setLoading(true);
    Object.values(docBlobUrls).forEach((url) => URL.revokeObjectURL(url));
    setDocBlobUrls({});
    setDocBlobLoading({});
    setDocTabs([]);
    setActiveTabId('agenda');
    
    // Initial data fetch
    fetchLiveStateRef.current();
    fetchAttendanceRef.current();
    fetchBoardMembersRef.current();
    fetchMeetingDocuments();
    fetchEditStatus();
    
    // Poll live state every 5 seconds (increased from 2s to reduce rate limit hits)
    pollingRef.current = setInterval(() => {
      fetchLiveStateRef.current();
    }, 5000);
    
    // Poll attendance less frequently (every 15 seconds)
    attendancePollingRef.current = setInterval(() => {
      fetchAttendanceRef.current();
    }, 15000);
    
    return () => {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
      }
      if (attendancePollingRef.current) {
        clearInterval(attendancePollingRef.current);
      }
    };
  }, [meetingId]);
  
  // Actions
  const startMeeting = async () => {
    setActionLoading(true);
    try {
      await api.post(`/board/meetings/${meetingId}/start`);
      toast.success('Meeting started!');
      fetchLiveState();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to start meeting');
    } finally {
      setActionLoading(false);
    }
  };
  
  const endMeeting = async () => {
    if (!window.confirm('Are you sure you want to end this meeting?')) return;
    
    setActionLoading(true);
    try {
      await api.post(`/board/meetings/${meetingId}/end`);
      toast.success('Meeting ended');
      fetchLiveState();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to end meeting');
    } finally {
      setActionLoading(false);
    }
  };
  
  const pauseMeeting = async () => {
    setActionLoading(true);
    try {
      const response = await api.post(`/board/meetings/${meetingId}/pause`);
      const pausedTitle = response.data.paused_item_title;
      if (pausedTitle) {
        toast.info(`Meeting paused. "${pausedTitle}" saved.`);
      } else {
        toast.info('Meeting paused');
      }
      fetchLiveStateRef.current();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to pause meeting');
    } finally {
      setActionLoading(false);
    }
  };
  
  const resumeMeeting = async () => {
    setActionLoading(true);
    try {
      const response = await api.post(`/board/meetings/${meetingId}/resume`);
      const resumedTitle = response.data.resumed_item_title;
      if (resumedTitle) {
        toast.success(`Resumed "${resumedTitle}"`);
      } else {
        toast.success('Meeting resumed');
      }
      fetchLiveStateRef.current();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to resume meeting');
    } finally {
      setActionLoading(false);
    }
  };
  
  const activateItem = async (itemId) => {
    setActionLoading(true);
    try {
      await api.post(`/board/meetings/${meetingId}/agenda/${itemId}/activate`);
      fetchLiveStateRef.current();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to activate item');
    } finally {
      setActionLoading(false);
    }
  };
  
  const completeItem = async (itemId) => {
    setActionLoading(true);
    try {
      await api.post(`/board/meetings/${meetingId}/agenda/${itemId}/complete`);
      toast.success('Item completed');
      fetchLiveState();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to complete item');
    } finally {
      setActionLoading(false);
    }
  };
  
  const skipItem = async (itemId) => {
    setActionLoading(true);
    try {
      await api.post(`/board/meetings/${meetingId}/agenda/${itemId}/skip`);
      toast.info('Item skipped');
      fetchLiveState();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to skip item');
    } finally {
      setActionLoading(false);
    }
  };
  
  const deferItem = async (itemId) => {
    setActionLoading(true);
    try {
      await api.post(`/board/meetings/${meetingId}/agenda/${itemId}/defer`);
      toast.info('Item deferred');
      fetchLiveState();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to defer item');
    } finally {
      setActionLoading(false);
    }
  };
  
  // Note-taking mode functions
  const releaseNotesLock = useCallback(async () => {
    if (!hasNotesLock) return;
    try {
      await api.delete(`/board/meetings/${meetingId}/minutes/lock`);
    } catch (err) {
      console.warn('Failed to release notes lock:', err);
    } finally {
      setHasNotesLock(false);
    }
  }, [hasNotesLock, meetingId]);

  const acquireNotesLock = async () => {
    try {
      await api.post(`/board/meetings/${meetingId}/minutes/lock`);
      setHasNotesLock(true);
      setNotesReadOnly(false);
      return true;
    } catch (err) {
      const message = err.response?.data?.detail || 'Notes are locked by another user';
      if (err.response?.status === 423) {
        toast.warning(message);
      } else {
        toast.error(message);
      }
      setNotesReadOnly(true);
      setHasNotesLock(false);
      return false;
    }
  };

  const enableNoteTakingMode = async () => {
    const lockAcquired = await acquireNotesLock();
    setNoteTakingMode(true);
    setNotesReadOnly(!lockAcquired);
    // Fetch existing notes for all agenda items
    try {
      const response = await api.get(`/board/meetings/${meetingId}/minutes`);
      const notesMap = {};
      response.data.agenda_notes?.forEach(note => {
        notesMap[note.agenda_item_id] = note.notes || '';
      });
      setAgendaNotes(notesMap);
      setMinutesReviewDocumentId(response.data?.minutes?.pdf_document_id || null);
    } catch (err) {
      console.error('Failed to fetch notes:', err);
    }
  };
  
  const handleNotesChange = (itemId, notes) => {
    setAgendaNotes(prev => ({ ...prev, [itemId]: notes }));
  };
  
  const saveAgendaItemNotes = async (itemId, notes) => {
    if (notesReadOnly) {
      toast.warning('Notes are locked by another user');
      return;
    }
    setSavingNotes(prev => ({ ...prev, [itemId]: true }));
    try {
      await api.put(`/board/meetings/${meetingId}/agenda/${itemId}/notes`, {
        notes: notes
      });
      toast.success('Notes saved');
    } catch (err) {
      const errorMessage = err.response?.data?.detail || 'Failed to save notes';
      toast.error(errorMessage);
    } finally {
      setSavingNotes(prev => ({ ...prev, [itemId]: false }));
    }
  };

  useEffect(() => {
    return () => {
      releaseNotesLock();
    };
  }, [releaseNotesLock]);

  const handleTranscriptFileUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    try {
      const text = await file.text();
      setTranscriptText(text);
      setTranscriptSourceName(file.name);
      toast.success('Transcript loaded');
    } catch (err) {
      toast.error('Failed to read transcript file');
    } finally {
      event.target.value = '';
    }
  };

  const generateTranscriptDraft = async () => {
    if (!transcriptText.trim()) {
      toast.error('Paste or upload a transcript first');
      return;
    }
    setGeneratingTranscriptDraft(true);
    try {
      const res = await api.post(`/board/meetings/${meetingId}/minutes/transcript-draft`, {
        transcript_text: transcriptText,
        source_name: transcriptSourceName || null
      });
      setTranscriptDraft(res.data.draft);
      toast.success('Transcript draft generated');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to generate transcript draft');
    } finally {
      setGeneratingTranscriptDraft(false);
    }
  };

  const applyTranscriptDraft = async () => {
    if (!transcriptDraft) return;
    setApplyingTranscriptDraft(true);
    try {
      const res = await api.post(`/board/meetings/${meetingId}/minutes/apply-transcript-draft`, {
        draft: transcriptDraft,
        replace_existing_notes: replaceExistingTranscriptNotes,
        create_action_items: createTranscriptActionItems
      });
      toast.success(`Draft applied: ${res.data.agenda_notes_applied || 0} agenda notes, ${res.data.action_items_created || 0} action items`);
      setShowTranscriptDialog(false);
      setTranscriptDraft(null);
      // Refresh live agenda notes so note mode reflects newly applied draft content.
      const notesRes = await api.get(`/board/meetings/${meetingId}/minutes`);
      const notesMap = {};
      (notesRes.data?.agenda_notes || []).forEach(note => {
        notesMap[note.agenda_item_id] = note.notes || '';
      });
      setAgendaNotes(notesMap);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to apply transcript draft');
    } finally {
      setApplyingTranscriptDraft(false);
    }
  };

  const generateMinutesReviewDocument = async () => {
    if (generatingMinutesReviewDoc) return;
    setGeneratingMinutesReviewDoc(true);
    try {
      const res = await api.post(`/board/meetings/${meetingId}/minutes/review-document`);
      toast.success(res.data?.message || 'Minutes review document updated');
      if (res.data?.document_id) {
        setMinutesReviewDocumentId(res.data.document_id);
        await fetchMeetingDocuments();
        handleOpenDocumentById(res.data.document_id, res.data.file_name || 'Draft Minutes for Review', { preferFresh: true });
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to generate minutes review document');
    } finally {
      setGeneratingMinutesReviewDoc(false);
    }
  };

  const generateFollowupTasks = async () => {
    if (generatingFollowupTasks) return;
    setGeneratingFollowupTasks(true);
    try {
      const res = await api.post(`/board/meetings/${meetingId}/generate-followup-tasks`);
      const { created = 0, updated = 0, skipped = 0, orphaned = 0 } = res.data || {};
      toast.success(`Follow-up tasks generated: ${created} created, ${updated} updated`);
      if (skipped || orphaned) {
        toast.message(`Skipped: ${skipped} · Orphaned: ${orphaned}`);
      }
    } catch (err) {
      const errorMessage = err.response?.data?.detail || 'Failed to generate follow-up tasks';
      toast.error(errorMessage);
    } finally {
      setGeneratingFollowupTasks(false);
    }
  };
  
  const goBack = () => {
    navigate('/governance');
  };
  
  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-purple-600" />
      </div>
    );
  }
  
  // Error state
  if (error || !meeting) {
    return (
      <div className="min-h-screen bg-gray-100 flex flex-col items-center justify-center">
        <AlertCircle className="h-12 w-12 text-red-500 mb-4" />
        <p className="text-gray-500 mb-4">{error || 'Meeting not found'}</p>
        <Button onClick={goBack} variant="outline">
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Governance
        </Button>
      </div>
    );
  }
  
  const isLive = meeting.status === 'in_progress';
  const isCompleted = meeting.status === 'completed';
  const isScheduled = meeting.status === 'scheduled';
  // Meeting is paused when in General/Admin mode with no active agenda item
  const isPaused = isLive && meeting.is_in_general_admin && !meeting.current_agenda_item_id;
  
  return (
    <div className="h-screen bg-gray-100 flex flex-col overflow-hidden" data-testid="live-meeting-runner">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 px-6 py-3 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="sm" onClick={goBack}>
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back
            </Button>
            
            <div className="h-6 w-px bg-gray-200" />
            
            <div>
              <div className="flex items-center gap-2">
                <h1 className="font-semibold text-gray-900">{meeting.title}</h1>
                {isLive && <Badge className="bg-red-500 text-white animate-pulse">LIVE</Badge>}
                {isCompleted && <Badge variant="secondary">Completed</Badge>}
                {isScheduled && <Badge className="bg-purple-500 text-white">Scheduled</Badge>}
              </div>
              {isScheduled && (
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={() => {
                    setEditData({
                      title: meeting.title || '',
                      description: meeting.description || '',
                      scheduled_start: meeting.scheduled_start ? new Date(meeting.scheduled_start).toISOString().slice(0, 16) : '',
                      scheduled_end: meeting.scheduled_end ? new Date(meeting.scheduled_end).toISOString().slice(0, 16) : '',
                      location: meeting.location || '',
                      location_type: meeting.location_type || 'in_person',
                      video_link: meeting.video_link || '',
                      meeting_type: meeting.meeting_type || 'regular'
                    });
                    setShowEditMeeting(true);
                  }}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <Edit2 className="h-4 w-4 mr-1" />
                  Edit
                </Button>
              )}
            </div>
          </div>
          
          <div className="flex items-center gap-3">
            {/* Reopen Meeting - for completed meetings (hidden if minutes approved) */}
            {isCompleted && !meeting?.minutes_approved && (
              <Button 
                variant="outline" 
                size="sm" 
                onClick={async () => {
                  if (!window.confirm('Reopen this meeting to continue? All previous data will be preserved.')) {
                    return;
                  }
                  try {
                    setActionLoading(true);
                    await api.post(`/board/meetings/${meetingId}/reopen`);
                    toast.success('Meeting reopened - you can continue from where you left off');
                    fetchLiveState();
                  } catch (err) {
                    toast.error(err.response?.data?.detail || 'Failed to reopen meeting');
                    console.error(err);
                  } finally {
                    setActionLoading(false);
                  }
                }}
                className="text-green-600 border-green-300 hover:bg-green-50 text-xs"
                disabled={actionLoading}
                title="Reopen meeting to continue from where you left off"
              >
                <RefreshCw className="h-3.5 w-3.5 mr-1" />
                Reopen Meeting
              </Button>
            )}
            
            {/* Show locked badge if minutes are approved */}
            {isCompleted && meeting?.minutes_approved && (
              <Badge variant="outline" className="text-xs bg-slate-100 text-slate-600 border-slate-300">
                🔒 Minutes Approved - Record Locked
              </Badge>
            )}
            
            {/* Reset Meeting - only show for in-progress meetings (accidental start) */}
            {isLive && (
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={async () => {
                  if (!window.confirm('Are you sure you want to RESET this meeting? This will clear all timing data and set the meeting back to scheduled. Use this only if the meeting was started by accident.')) {
                    return;
                  }
                  try {
                    setActionLoading(true);
                    await api.post(`/board/meetings/${meetingId}/reset`);
                    toast.success('Meeting has been reset to scheduled');
                    fetchLiveState();
                  } catch (err) {
                    // Show the specific error message from backend
                    const errorMessage = err.response?.data?.detail || 'Failed to reset meeting';
                    toast.error(errorMessage);
                    console.error(err);
                  } finally {
                    setActionLoading(false);
                  }
                }}
                className="text-orange-500 hover:text-orange-600 hover:bg-orange-50 text-xs"
                disabled={actionLoading}
                title="Reset meeting (use if started by accident)"
              >
                <RotateCcw className="h-3.5 w-3.5 mr-1" />
                Reset
              </Button>
            )}
            
            {/* Quorum Indicator */}
            {isLive && quorum && (
              <QuorumIndicator 
                quorum={quorum} 
                onClick={() => setShowAttendance(true)}
              />
            )}
            
            {/* Meeting Clock */}
            <MeetingClock 
              serverElapsed={timing.meeting_elapsed_seconds || 0}
              isLive={isLive}
              isPaused={isPaused}
            />
            
            {/* Control Buttons */}
            {isChair && (
              <>
                {isScheduled && (
                  <Button 
                    onClick={startMeeting}
                    disabled={actionLoading}
                    className="bg-green-600 hover:bg-green-700"
                  >
                    {actionLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Play className="h-4 w-4 mr-2" />}
                    Start Meeting
                  </Button>
                )}
                
                {isLive && (
                  <>
                    <Button 
                      onClick={isPaused ? resumeMeeting : pauseMeeting}
                      disabled={actionLoading}
                      variant="outline"
                      size="icon"
                      className={`h-9 w-9 ${isPaused ? 'border-green-500 text-green-600 hover:bg-green-50' : 'border-amber-500 text-amber-600 hover:bg-amber-50'}`}
                      title={isPaused ? "Resume Meeting" : "Pause Meeting"}
                    >
                      {actionLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : (isPaused ? <Play className="h-4 w-4" /> : <Pause className="h-4 w-4" />)}
                    </Button>
                    <Button 
                      onClick={endMeeting}
                      disabled={actionLoading}
                      variant="destructive"
                    >
                      {actionLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Square className="h-4 w-4 mr-2" />}
                      End Meeting
                    </Button>
                  </>
                )}
              </>
            )}
          </div>
        </div>
      </header>
      
      {/* Attendance Panel Dialog */}
      <AttendancePanel
        open={showAttendance}
        onClose={() => setShowAttendance(false)}
        meetingId={meetingId}
        isChair={isChair}
        onUpdate={fetchAttendance}
      />

      {/* Main Content */}
      <div className="flex-1 w-full px-6 py-6 min-h-0 flex flex-col overflow-hidden">
        <ResizableMeetingLayout
          showLeft={true}
          className="flex-1 min-h-0"
          defaultWidths={{ left: 18, center: 64, right: 18 }}
          defaultLeftCollapsed={false}
          maxSideWidthPx={320}
          minRightWidthPercent={18}
          leftContent={
            <div className="pr-4 h-full">
              <MeetingQuickLinks
                meetingId={meetingId}
                onNavigate={(url) => navigate(url)}
                onOpenDocument={handleOpenDocumentById}
                className="h-full"
              />
            </div>
          }
          centerContent={
            <div className="px-3 h-full min-h-0">
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden flex flex-col h-full min-h-0">
                <div className="flex items-center border-b border-gray-100 bg-white">
                  <button
                    className={`px-4 py-3 text-sm font-medium border-b-2 ${
                      activeTabId === 'agenda'
                        ? 'border-purple-600 text-purple-700'
                        : 'border-transparent text-gray-600 hover:text-gray-900'
                    }`}
                    onClick={() => setActiveTabId('agenda')}
                  >
                    Agenda
                  </button>
                  {docTabs.map((doc) => (
                    <button
                      key={doc.document_id}
                      className={`px-3 py-3 text-sm font-medium border-b-2 max-w-[200px] flex items-center gap-2 ${
                        activeTabId === doc.document_id
                          ? 'border-purple-600 text-purple-700'
                          : 'border-transparent text-gray-600 hover:text-gray-900'
                      }`}
                      onClick={() => setActiveTabId(doc.document_id)}
                      title={doc.title}
                    >
                      <span className="truncate">{doc.title}</span>
                      <span
                        className="text-gray-400 hover:text-gray-600 flex-shrink-0"
                        onClick={(e) => {
                          e.stopPropagation();
                          closeDocTab(doc.document_id);
                        }}
                      >
                        ×
                      </span>
                    </button>
                  ))}
                </div>

                {activeTabId === 'agenda' ? (
                  <div className="flex-1 overflow-auto min-h-0">
                    <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
                      <h2 className="font-semibold text-gray-900 flex items-center gap-2">
                        <ClipboardList className="h-5 w-5 text-purple-600" />
                        Agenda
                      </h2>
                      <span className="text-sm text-gray-500">
                        {agendaItems.filter(i => i.status === 'completed').length} of {agendaItems.length} completed
                      </span>
                    </div>
                    
                    {/* General Admin Time */}
                    <GeneralAdminIndicator 
                      isActive={isLive && meeting.is_in_general_admin && !meeting.current_agenda_item_id}
                      seconds={timing.general_admin_seconds || 0}
                    />
                    
                    {/* Agenda Items */}
                    {agendaItems.length === 0 ? (
                      <div className="p-8 text-center text-gray-500">
                        <ClipboardList className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                        <p className="mb-4">No agenda items yet</p>
                        <Button 
                          onClick={() => setShowAgendaCreator(true)}
                          className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700"
                        >
                          <Plus className="h-4 w-4 mr-2" />
                          Add Agenda
                        </Button>
                      </div>
                    ) : (
                      agendaItems
                        .sort((a, b) => a.sort_order - b.sort_order)
                        .map((item, index) => (
                          <AgendaItemRow
                            key={item.id}
                            item={item}
                            index={index}
                            isActive={item.is_active}
                            isChair={isChair && isLive}
                            onActivate={activateItem}
                            onComplete={completeItem}
                            onSkip={skipItem}
                            onDefer={deferItem}
                            serverElapsed={item.is_active ? timing.current_item_elapsed_seconds : 0}
                            // Note-taking mode props
                            noteTakingMode={noteTakingMode}
                            notes={agendaNotes[item.id] || ''}
                            onNotesChange={handleNotesChange}
                            onSaveNotes={saveAgendaItemNotes}
                            isSavingNotes={savingNotes[item.id] || false}
                            readOnlyNotes={notesReadOnly}
                            // Motions for this agenda item
                            motions={item.motions || []}
                            attachments={linkedDocsByAgenda[item.id] || []}
                            onOpenAttachment={handleOpenDocumentById}
                          />
                        ))
                    )}
                  </div>
                ) : (
                  <div className="flex-1 overflow-hidden bg-gray-100 min-h-0">
                    {docTabs.length === 0 ? (
                      <div className="flex flex-col items-center justify-center h-full text-gray-500 py-12">
                        <FileText className="h-12 w-12 mb-3 text-gray-300" />
                        <p>No documents open</p>
                      </div>
                    ) : (
                      (() => {
                        const activeDoc = docTabs.find((doc) => doc.document_id === activeTabId);
                        if (!activeDoc) {
                          return (
                            <div className="flex flex-col items-center justify-center h-full text-gray-500 py-12">
                              <FileText className="h-12 w-12 mb-3 text-gray-300" />
                              <p>Select a document tab</p>
                            </div>
                          );
                        }
                        const previewUrl = docBlobUrls[activeDoc.document_id];
                        const isPreviewLoading = docBlobLoading[activeDoc.document_id] && !previewUrl;
                        return (
                          <div className="h-full flex flex-col min-h-0">
                            <div className="bg-white border-b p-3 flex items-center justify-between">
                              <div className="flex items-center gap-2 min-w-0">
                                <FileText className="h-4 w-4 text-purple-600" />
                                <span className="font-medium truncate">{activeDoc.title}</span>
                              </div>
                              {previewUrl && (
                                <a
                                  href={previewUrl}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-sm text-purple-600 hover:underline"
                                >
                                  Open in new tab ↗
                                </a>
                              )}
                            </div>
                            <div className="flex-1 bg-gray-800 min-h-0">
                              {isPreviewLoading ? (
                                <div className="flex flex-col items-center justify-center h-full text-white">
                                  <Loader2 className="h-10 w-10 mb-3 animate-spin opacity-70" />
                                  <p>Loading document...</p>
                                </div>
                              ) : previewUrl && (isPdfType(activeDoc.file_type) || activeDoc.preview_url) ? (
                                <MeetingPdfPreview url={previewUrl} title={activeDoc.title} />
                              ) : previewUrl && isImageType(activeDoc.file_type) ? (
                                <div className="flex items-center justify-center h-full p-4 bg-white">
                                  <img
                                    src={previewUrl}
                                    alt={activeDoc.title}
                                    className="max-w-full max-h-full object-contain"
                                  />
                                </div>
                              ) : (
                                <div className="flex flex-col items-center justify-center h-full text-white">
                                  <FileText className="h-16 w-16 mb-4 opacity-50" />
                                  <p className="text-lg mb-2">Preview not available</p>
                                  {previewUrl && (
                                    <a
                                      href={previewUrl}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="px-4 py-2 bg-purple-600 rounded-lg hover:bg-purple-700"
                                    >
                                      Open Document
                                    </a>
                                  )}
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      })()
                    )}
                  </div>
                )}
              </div>
            </div>
          }
          rightContent={
            <div className="pl-3 space-y-6">
              {/* Meeting Info */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <Calendar className="h-5 w-5 text-purple-600" />
                  Meeting Info
                </h3>
                
                <div className="space-y-3 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-500">Status</span>
                    <span className="font-medium capitalize">{meeting.status?.replace('_', ' ')}</span>
                  </div>
                  {meeting.meeting_started_at && (
                    <div className="flex justify-between">
                      <span className="text-gray-500">Started</span>
                      <span className="font-medium">{formatTime(meeting.meeting_started_at)}</span>
                    </div>
                  )}
                  {meeting.actual_end && (
                    <div className="flex justify-between">
                      <span className="text-gray-500">Ended</span>
                      <span className="font-medium">{formatTime(meeting.actual_end)}</span>
                    </div>
                  )}
                  <div className="flex justify-between">
                    <span className="text-gray-500">Quorum Required</span>
                    <span className="font-medium">{quorum?.required ?? meeting.quorum_required ?? 0}</span>
                  </div>
                </div>
              </div>

              {/* Time Summary */}
              {(isLive || isCompleted) && (
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                  <h3 className="font-semibold text-gray-900 mb-4 flex items-center justify-between">
                    <span className="flex items-center gap-2">
                      <Timer className="h-5 w-5 text-purple-600" />
                      Time Summary
                    </span>
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      className="text-purple-600 hover:text-purple-700"
                      onClick={() => setShowTimeAnalytics(true)}
                    >
                      View Analytics
                    </Button>
                  </h3>
                  
                  <div className="space-y-3 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-500">Meeting Duration</span>
                      <span className="font-mono font-medium">
                        {formatDuration(timing.meeting_elapsed_seconds || 0)}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">General/Admin</span>
                      <span className="font-mono font-medium">
                        {formatDuration(timing.general_admin_seconds || 0)}
                      </span>
                    </div>
                    
                    <div className="border-t border-gray-100 pt-3 mt-3">
                      <p className="text-xs text-gray-400 mb-2">Agenda Items</p>
                      {agendaItems.filter(i => i.time_spent_seconds > 0).map(item => (
                        <div key={item.id} className="flex justify-between text-xs py-1">
                          <span className="text-gray-600 truncate mr-2">{item.title}</span>
                          <span className="font-mono text-gray-500">{formatDuration(item.time_spent_seconds)}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* Quick Actions */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <h3 className="font-semibold text-gray-900 mb-4">Quick Actions</h3>
                
                <div className="space-y-2">
                  <Button 
                    variant="outline" 
                    className="w-full justify-start" 
                    onClick={() => setShowAttendance(true)}
                  >
                    <Users className="h-4 w-4 mr-2" />
                    Take Attendance
                  </Button>
                  <Button 
                    variant={noteTakingMode ? "default" : "outline"}
                    className={`w-full justify-start ${noteTakingMode ? 'bg-purple-600 text-white' : ''}`}
                    onClick={noteTakingMode ? async () => {
                      setNoteTakingMode(false);
                      await releaseNotesLock();
                      setNotesReadOnly(false);
                    } : enableNoteTakingMode}
                  >
                    <PenLine className="h-4 w-4 mr-2" />
                    {noteTakingMode ? 'Exit Note Mode' : 'Take Notes'}
                  </Button>
                  <Button
                    variant="outline"
                    className="w-full justify-start"
                    onClick={() => setShowTranscriptDialog(true)}
                  >
                    <Upload className="h-4 w-4 mr-2" />
                    Transcript Draft
                  </Button>
                  {noteTakingMode && (
                    <Button
                      variant="outline"
                      className="w-full justify-start"
                      onClick={generateMinutesReviewDocument}
                      disabled={generatingMinutesReviewDoc}
                    >
                      {generatingMinutesReviewDoc ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <FileText className="h-4 w-4 mr-2" />}
                      {minutesReviewDocumentId ? 'Update Minutes Review Doc' : 'Generate Minutes Review Doc'}
                    </Button>
                  )}
                  {canGenerateFollowupTasks && (
                    <Button
                      variant="outline"
                      className="w-full justify-start"
                      onClick={generateFollowupTasks}
                      disabled={generatingFollowupTasks}
                    >
                      {generatingFollowupTasks ? (
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      ) : (
                        <ClipboardList className="h-4 w-4 mr-2" />
                      )}
                      Generate Follow-up Tasks
                    </Button>
                  )}
                  <Button 
                    variant="outline" 
                    className="w-full justify-start"
                    onClick={() => setShowMotions(true)}
                  >
                    <ClipboardList className="h-4 w-4 mr-2" />
                    Motions & Voting
                  </Button>
                  <Button 
                    variant="outline" 
                    className="w-full justify-start"
                    onClick={handleOpenFirstMeetingDoc}
                  >
                    <FileText className="h-4 w-4 mr-2" />
                    View Documents
                  </Button>
                  <Button 
                    variant="outline" 
                    className="w-full justify-start"
                    onClick={() => setShowParkingLot(true)}
                  >
                    <MessageSquare className="h-4 w-4 mr-2" />
                    Parking Lot
                  </Button>
                  {(isLive || isCompleted) && (
                    <Button 
                      variant="outline" 
                      className="w-full justify-start"
                      onClick={() => setShowTimeAnalytics(true)}
                    >
                      <BarChart3 className="h-4 w-4 mr-2" />
                      Time Analytics
                    </Button>
                  )}
                </div>
              </div>
            </div>
          }
        />
      </div>
      
      <Dialog open={showTranscriptDialog} onOpenChange={setShowTranscriptDialog}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-purple-600" />
              Transcript Draft
            </DialogTitle>
            <DialogDescription>
              Paste or upload a text transcript. Athena will compare it against this meeting's agenda and draft agenda notes/action items for review.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="flex items-center justify-between gap-3">
              <div className="text-sm text-gray-600">
                Supports .txt, .md, .vtt, and .srt transcript files.
                {transcriptSourceName && <span className="block text-purple-700 mt-1">Loaded: {transcriptSourceName}</span>}
              </div>
              <label className="inline-flex items-center gap-2 px-3 py-2 rounded-md border border-purple-200 bg-white text-sm font-medium text-purple-700 cursor-pointer hover:bg-purple-50">
                <Upload className="h-4 w-4" />
                Upload transcript
                <input type="file" accept=".txt,text/plain,.md,.vtt,.srt" className="hidden" onChange={handleTranscriptFileUpload} />
              </label>
            </div>

            <Textarea
              value={transcriptText}
              onChange={(e) => setTranscriptText(e.target.value)}
              placeholder="Paste meeting transcript here..."
              className="min-h-[180px]"
              disabled={generatingTranscriptDraft}
            />

            <div className="flex items-center justify-between gap-3">
              <span className="text-xs text-gray-500">{transcriptText.trim().length.toLocaleString()} characters</span>
              <Button onClick={generateTranscriptDraft} disabled={generatingTranscriptDraft || !transcriptText.trim()}>
                {generatingTranscriptDraft ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Sparkles className="h-4 w-4 mr-2" />}
                Generate Draft
              </Button>
            </div>

            {transcriptDraft && (
              <div className="space-y-4 border-t pt-4">
                <div className="grid grid-cols-3 gap-3 text-sm">
                  <div className="rounded-lg bg-purple-50 p-3">
                    <div className="font-semibold text-purple-900">Agenda notes</div>
                    <div className="text-2xl font-bold text-purple-700">{transcriptDraft.agenda_notes?.length || 0}</div>
                  </div>
                  <div className="rounded-lg bg-blue-50 p-3">
                    <div className="font-semibold text-blue-900">Action items</div>
                    <div className="text-2xl font-bold text-blue-700">{transcriptDraft.action_items?.length || 0}</div>
                  </div>
                  <div className="rounded-lg bg-amber-50 p-3">
                    <div className="font-semibold text-amber-900">Warnings</div>
                    <div className="text-2xl font-bold text-amber-700">{transcriptDraft.warnings?.length || 0}</div>
                  </div>
                </div>

                <div className="max-h-72 overflow-y-auto space-y-3 pr-1">
                  {(transcriptDraft.agenda_notes || []).map(note => {
                    const agendaItem = agendaItems.find(item => item.id === note.agenda_item_id);
                    return (
                      <div key={note.agenda_item_id} className="border rounded-lg p-3">
                        <div className="flex items-center justify-between gap-2 mb-2">
                          <div className="font-medium text-sm text-gray-900">{agendaItem ? `${agendaItem.sort_order}. ${agendaItem.title}` : 'Agenda item'}</div>
                          <Badge variant="outline" className="capitalize">{note.confidence || 'medium'} confidence</Badge>
                        </div>
                        <p className="text-sm text-gray-700 whitespace-pre-wrap">{note.notes || 'No note drafted.'}</p>
                      </div>
                    );
                  })}
                </div>

                <div className="space-y-2 text-sm text-gray-700">
                  <label className="flex items-center gap-2">
                    <input type="checkbox" checked={replaceExistingTranscriptNotes} onChange={(e) => setReplaceExistingTranscriptNotes(e.target.checked)} />
                    Replace existing agenda notes instead of filling blanks only
                  </label>
                  <label className="flex items-center gap-2">
                    <input type="checkbox" checked={createTranscriptActionItems} onChange={(e) => setCreateTranscriptActionItems(e.target.checked)} />
                    Create action items from draft
                  </label>
                </div>

                <div className="flex justify-end gap-2">
                  <Button variant="outline" onClick={() => setTranscriptDraft(null)}>Discard Draft</Button>
                  <Button onClick={applyTranscriptDraft} disabled={applyingTranscriptDraft}>
                    {applyingTranscriptDraft ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <CheckCircle className="h-4 w-4 mr-2" />}
                    Apply Draft to Notes
                  </Button>
                </div>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Motions Panel */}
      <MotionsPanel
        meetingId={meetingId}
        agendaItems={agendaItems}
        isOpen={showMotions}
        onClose={() => setShowMotions(false)}
        isLive={isLive}
        boardMembers={boardMembers}
        currentAgendaItemId={meeting?.current_agenda_item_id}
      />
      
      {/* Parking Lot Panel */}
      <ParkingLotPanel
        meetingId={meetingId}
        agendaItems={agendaItems}
        isOpen={showParkingLot}
        onClose={() => setShowParkingLot(false)}
        isLive={isLive}
      />
      
      {/* Time Analytics Panel */}
      <TimeAnalyticsPanel
        meetingId={meetingId}
        meeting={meeting}
        agendaItems={agendaItems}
        timing={timing}
        isOpen={showTimeAnalytics}
        onClose={() => setShowTimeAnalytics(false)}
      />
      
      {/* Agenda Creator Dialog */}
      <CreateAgendaDialog
        open={showAgendaCreator}
        onOpenChange={setShowAgendaCreator}
        preSelectedMeetingId={meetingId}
        onSuccess={() => {
          setShowAgendaCreator(false);
          toast.success('Agenda created successfully');
          fetchLiveState();
        }}
      />
      
      {/* Edit Meeting Dialog */}
      <Dialog open={showEditMeeting} onOpenChange={setShowEditMeeting}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Edit Meeting</DialogTitle>
            <DialogDescription>Update meeting details, date, time, and location</DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 mt-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
              <Input
                value={editData.title || ''}
                onChange={(e) => setEditData({...editData, title: e.target.value})}
                placeholder="Meeting title"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
              <textarea
                value={editData.description || ''}
                onChange={(e) => setEditData({...editData, description: e.target.value})}
                placeholder="Meeting description (optional)"
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                rows={2}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Start Date & Time</label>
                <Input
                  type="datetime-local"
                  value={editData.scheduled_start || ''}
                  onChange={(e) => setEditData({...editData, scheduled_start: e.target.value})}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">End Date & Time</label>
                <Input
                  type="datetime-local"
                  value={editData.scheduled_end || ''}
                  onChange={(e) => setEditData({...editData, scheduled_end: e.target.value})}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Meeting Type</label>
                <Select value={editData.meeting_type || 'regular'} onValueChange={(v) => setEditData({...editData, meeting_type: v})}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="regular">Regular</SelectItem>
                    <SelectItem value="special">Special</SelectItem>
                    <SelectItem value="annual">Annual</SelectItem>
                    <SelectItem value="emergency">Emergency</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Location Type</label>
                <Select value={editData.location_type || 'in_person'} onValueChange={(v) => setEditData({...editData, location_type: v})}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="in_person">In Person</SelectItem>
                    <SelectItem value="virtual">Virtual</SelectItem>
                    <SelectItem value="hybrid">Hybrid</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Location / Address</label>
              <Input
                value={editData.location || ''}
                onChange={(e) => setEditData({...editData, location: e.target.value})}
                placeholder="Physical location or address"
              />
            </div>

            {(editData.location_type === 'virtual' || editData.location_type === 'hybrid') && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Video Link</label>
                <Input
                  value={editData.video_link || ''}
                  onChange={(e) => setEditData({...editData, video_link: e.target.value})}
                  placeholder="Zoom, Teams, or Google Meet link"
                />
              </div>
            )}
          </div>

          <div className="flex justify-end gap-3 mt-6">
            <Button variant="outline" onClick={() => setShowEditMeeting(false)}>Cancel</Button>
            <Button 
              onClick={async () => {
                try {
                  setSaving(true);
                  const updates = {};
                  if (editData.title) updates.title = editData.title;
                  if (editData.description !== undefined) updates.description = editData.description;
                  if (editData.scheduled_start) updates.scheduled_start = new Date(editData.scheduled_start).toISOString();
                  if (editData.scheduled_end) updates.scheduled_end = new Date(editData.scheduled_end).toISOString();
                  if (editData.location !== undefined) updates.location = editData.location;
                  if (editData.location_type) updates.location_type = editData.location_type;
                  if (editData.video_link !== undefined) updates.video_link = editData.video_link;
                  if (editData.meeting_type) updates.meeting_type = editData.meeting_type;

                  await api.put(`/board/meetings/${meetingId}`, updates);
                  toast.success('Meeting updated successfully');
                  setShowEditMeeting(false);
                  fetchLiveState();
                } catch (err) {
                  const errorMessage = err.response?.data?.detail || 'Failed to update meeting';
                  toast.error(errorMessage);
                  console.error(err);
                } finally {
                  setSaving(false);
                }
              }} 
              disabled={saving || !editData.title || !editData.scheduled_start}
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Save Changes
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      
    </div>
  );
};

export default LiveMeetingRunner;
